import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { KrCard } from '@/common/card/KrCard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { useDialog } from '@/common/dialog/DialogContext';
import { Form } from '@/common/form/Form';
import { FormFields } from '@/common/form/rendering/FormFields';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { ContentBox } from '@/common/layout/ContentBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { SaleStatusDefinitionsDialog } from '@/sc/entities/indirSale/dashboard/SaleStatusDefinitionsDialog';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { ChbkFinGrowthCards } from './ChbkFinGrowthCards';
import { ChbkFinTrendChart } from './ChbkFinTrendChart';
import { ChbkFinTrendGraph } from './ChbkFinTrendGraph';
import { ChbkFinTrendTableCard } from './ChbkFinTrendTableCard';

export const ChbkFinDashboard = () => {
  const rsrc = useRsrc();

  const { openDialog } = useDialog();
  const { navState } = useNavState();

  const [fieldDefs, setFieldDefs] = useState();
  const [formValues, setFormValues] = useNavStateSync('formValues');

  const [totalValues, setTotalValues] = useState();

  const ctrtTypeIdToCtrtType = { 1: 'GPO', 2: 'PHS', 3: 'FSS' };

  useEffect(() => {
    const currentPeriodicity = formValues?.periodicity ?? 'MONTH';
    rsrc.data('sc', 'CHBK_FIN_DASHBOARD_FILTERS_DATA_PLUGIN', { periodicity: currentPeriodicity }).then((res) => {
      setFieldDefs([
        currentPeriodicity === 'DAY' ?
          { field: 'invDate', displayName: 'Invoice Date', type: 'DATE', variant: 'outlined', values: res.invDate }
        : { field: 'invPeriodId', displayName: 'Invoice Period', type: 'DROPDOWN', values: res.periods },
        {
          field: 'periodicity',
          displayName: 'Periodicity',
          type: 'DROPDOWN',
          values: [
            { value: 'DAY', displayName: 'Day' },
            { value: 'MONTH', displayName: 'Month' },
            { value: 'QUARTER', displayName: 'Quarter' },
          ],
        },
        { field: 'prodIds', displayName: 'Products', type: 'TREE', treeData: res.prodTreeData },
        { field: 'distCustIds', displayName: 'Distributors', type: 'TREE', treeData: res.distCustTreeData },
        { field: 'ctrtTypeIds', displayName: 'Contract Types', type: 'TREE', treeData: res.ctrtTypeTreeData },
        {
          field: 'agg',
          displayName: 'Aggregate Value',
          type: 'DROPDOWN',
          values: [
            { value: 'PAID_AMT', displayName: 'Paid Amount' },
            { value: 'NET_AMT', displayName: 'Net Amount' },
            { value: 'PAID_QTY', displayName: 'Paid Quantity' },
          ],
        },
      ]);

      setTotalValues({
        totalProdIds: res.prodIds,
        totalDistCustIds: res.distCustIds,
      });

      if (!navState.formValues || !res.periods || !res?.periods.map((p) => p.value).includes(formValues.invPeriodId)) {
        let dateValue = {};
        if (currentPeriodicity === 'DAY') {
          dateValue = { invDate: res.invDate };
        } else {
          dateValue = { invPeriodId: res.periods[0].value };
        }

        setFormValues({
          ...dateValue,
          prodIds: formValues?.prodIds ?? res.prodIds,
          distCustIds: formValues?.distCustIds ?? res.distCustIds,
          ctrtTypeIds: formValues?.ctrtTypeIds ?? [1, 2, 3],
          periodicity: formValues?.periodicity ?? 'MONTH',
          agg: formValues?.agg ?? 'PAID_AMT',
        });
      }
    });
  }, [formValues?.periodicity]);

  const openDefinitionsDialog = () => {
    openDialog(SaleStatusDefinitionsDialog);
  };

  const formatPeriodicity = (periodicity) => {
    switch (periodicity) {
      case 'DAY':
        return 'Daily';
      case 'MONTH':
        return 'Monthly';
      case 'QUARTER':
        return 'Quarterly';
      default:
        return '';
    }
  };

  if (!fieldDefs) {
    return;
  }

  return (
    <ContentBox>
      {/* Title */}
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Title icon={fas.faTasks} title="Chargebacks Financial Dashboard" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TitleButton
            key="comparePolFormulaButton"
            icon={fas.faInfoCircle}
            toolTip="View Definitions"
            onClick={openDefinitionsDialog}
          />
          <ExportButton />
        </Box>
      </Box>

      {/* Filters */}
      <Form formValues={formValues} setFormValues={setFormValues}>
        <FormFields fieldDefs={fieldDefs} container={FilterContainer} />
      </Form>
      <SpacerLayout>
        <Spacer vertical>
          {/* Row 1 */}
          <Spacer weights={[1, 1]}>
            <KrCard title={`${formatPeriodicity(formValues?.periodicity)} Paid Trends`} sx={{ flex: 1 }}>
              <ChbkFinTrendChart
                formValues={formValues}
                totalValues={totalValues}
                ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
              />
            </KrCard>
            <ChbkFinTrendTableCard
              formValues={formValues}
              totalValues={totalValues}
              ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
            />
          </Spacer>

          {/* Row 2 */}
          <Spacer weights={[3, 8]}>
            <ChbkFinGrowthCards
              formValues={formValues}
              totalValues={totalValues}
              ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
            />
            <KrCard title={(formValues?.periodicity === 'DAY' ? 'Week over Week' : 'YoY') + ' Paid Trends'}>
              <ChbkFinTrendGraph
                formValues={formValues}
                totalValues={totalValues}
                ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
              />
            </KrCard>
          </Spacer>
        </Spacer>
      </SpacerLayout>
    </ContentBox>
  );
};
